import React, { useMemo, useState } from 'react';
import { useLanguage } from '@/contexts/LanguageContext';
import { useActiveBanners728x90 } from '@/hooks/useBanners728x90';
import { useAuth } from '@/hooks/useAuth';
import { ExternalLink, Zap } from 'lucide-react';
import { OptimizedImage } from '@/components/OptimizedImage';
import { Button } from '@/components/ui/button';
import { Banner728x90Dialog } from '@/components/Banner728x90Dialog';
import { toast } from '@/hooks/use-toast';

export const AdBanner728x90: React.FC = () => {
  const { language } = useLanguage();
  const { user } = useAuth();
  const { data: banners, isLoading } = useActiveBanners728x90();
  const [dialogOpen, setDialogOpen] = useState(false);

  const reserveText = language === 'fr' ? 'Réserver cet espace publicitaire' :
                      language === 'es' ? 'Reservar este espacio publicitario' :
                      'Reserve this advertising space';
  
  const loginPrompt = language === 'fr' ? 'Connectez-vous pour réserver' :
                      language === 'es' ? 'Inicia sesión para reservar' :
                      'Login to reserve';

  const placeholderText = language === 'fr' ? 'Espace publicitaire 728×90' :
                          language === 'es' ? 'Espacio publicitario 728×90' :
                          'Ad space 728×90';

  // Select a random banner
  const selectedBanner = useMemo(() => {
    if (!banners || banners.length === 0) return null;
    return banners[Math.floor(Math.random() * banners.length)];
  }, [banners]);

  const handleReserveClick = () => {
    if (!user) {
      toast({
        title: loginPrompt,
        description: language === 'fr' ? 
          'Vous devez être connecté pour réserver cet espace publicitaire.' :
          language === 'es' ?
          'Debes iniciar sesión para reservar este espacio publicitario.' :
          'You must be logged in to reserve this advertising space.',
        variant: "destructive",
      });
      return;
    }
    setDialogOpen(true);
  };

  const handleClick = () => {
    if (selectedBanner?.website_url) {
      window.open(selectedBanner.website_url, '_blank', 'noopener,noreferrer');
    }
  };

  if (isLoading) {
    return (
      <div className="mb-6 flex flex-col items-center justify-center">
        <Button
          onClick={handleReserveClick}
          variant="ghost"
          size="sm"
          className="mb-2 text-xs text-slate-500 hover:text-primary"
          title={!user ? loginPrompt : reserveText}
        >
          <Zap className="w-3 h-3 mr-1" />
          {reserveText}
        </Button>
        <div className="w-full max-w-[728px] h-[90px] bg-slate-800/50 border border-slate-700 rounded animate-pulse" />
        <Banner728x90Dialog
          isOpen={dialogOpen}
          onClose={() => setDialogOpen(false)}
          onSuccess={() => setDialogOpen(false)}
        />
      </div>
    );
  }

  if (!selectedBanner) {
    return (
      <div className="mb-6 flex flex-col items-center justify-center">
        <Button
          onClick={handleReserveClick}
          variant="ghost"
          size="sm"
          className="mb-2 text-xs text-slate-500 hover:text-primary"
          title={!user ? loginPrompt : reserveText}
        >
          <Zap className="w-3 h-3 mr-1" />
          {reserveText}
        </Button>
        <div 
          className="w-full max-w-[728px] h-[90px] bg-slate-800/50 border border-slate-700 rounded flex items-center justify-center"
          style={{ aspectRatio: '728/90' }}
        >
          <p className="text-slate-400 text-sm font-mono">{placeholderText}</p>
        </div>
        <Banner728x90Dialog
          isOpen={dialogOpen}
          onClose={() => setDialogOpen(false)}
          onSuccess={() => setDialogOpen(false)}
        />
      </div>
    );
  }

  return (
    <div className="mb-6 flex flex-col items-center justify-center">
      <Button
        onClick={handleReserveClick}
        variant="ghost"
        size="sm"
        className="mb-2 text-xs text-slate-500 hover:text-primary"
        title={!user ? loginPrompt : reserveText}
      >
        <Zap className="w-3 h-3 mr-1" />
        {reserveText}
      </Button>
      <div
        onClick={handleClick}
        className={`relative w-full max-w-[728px] h-[90px] rounded overflow-hidden border border-slate-700 ${
          selectedBanner.website_url ? 'cursor-pointer hover:border-slate-500 transition-colors' : ''
        }`}
        style={{ aspectRatio: '728/90' }}
      >
        <OptimizedImage
          src={selectedBanner.image_url}
          alt={language === 'fr' ? 'Bannière publicitaire' : language === 'es' ? 'Banner publicitario' : 'Advertising banner'}
          width={728}
          height={90}
          loading="eager"
          className="w-full h-full object-cover"
          fallback={
            <div className="w-full h-full bg-slate-800/50 flex items-center justify-center">
              <p className="text-slate-400 text-sm font-mono">{placeholderText}</p>
            </div>
          }
        />
        {selectedBanner.website_url && (
          <div className="absolute top-2 right-2 bg-black/60 p-1 rounded">
            <ExternalLink className="w-3 h-3 text-white" />
          </div>
        )}
      </div>
      <Banner728x90Dialog
        isOpen={dialogOpen}
        onClose={() => setDialogOpen(false)}
        onSuccess={() => setDialogOpen(false)}
      />
    </div>
  );
};
